import React, { useState, useEffect } from "react";
import "./admin-courses.css";
import {
  isEmpty,
  capitalizeAll,
  slugify,
  truncateValue,
  convertDuration,
} from "../../../../core/utils/helpers";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Button from "../../../../ui/atoms/buttons/Button";
import NotFound from "../../../../ui/organisms/not-found/404";
import StatusComponent from "../../../../ui/atoms/status/StatusComponent";
//
import {
  onGetCoursesWithPaginationByStreamId,
  onGetCoursesReleased,
  onGetCoursesArchived,
} from "../../services/course";
import { schoolSlice } from "../../../../app/store/slices/schoolSlice";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";
import Swal from "sweetalert2";

const updated_at = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

const AdminCourses = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const params = useParams();
  const { id, title, st_id, st_title } = params;

  const connectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser.connectedUserData,
  );

  const dispatch = useDispatch();
  //
  const [coursesData, setCoursesData] = useState({});
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefresh, setIsRefresh] = useState(false);
  //
  const [selectedRows, setSelectedRows] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);

  const currentSchool = useSelector(
    (state) => state?.schoolState?.currentSchool,
  );

  const currentStream = useSelector(
    (state) => state?.schoolState?.currentStream,
  );

  const _school_id = currentSchool?.school_id;
  const _school_title = currentSchool?.school_title;
  const _stream_id = currentStream?.classe_id;
  const _stream_title = currentStream?.classe_title;

  useEffect(() => {
    if (
      _school_id !== id ||
      slugify(_school_title) !== title ||
      _stream_id !== st_id ||
      slugify(_stream_title) !== st_title
    ) {
      setCoursesData({});
    }
  }, [id, title]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const localData = {
      stream_id: _stream_id,
      rows_per_page: rowsPerPage,
      current_page: currentPage,
    };

    onGetCoursesWithPaginationByStreamId(axiosPrivate, localData, signal).then((response) => {
      const _data = response?.data;
      setCoursesData(_data);
    });

    return () => {
      isMounted = false;
      isMounted && controller.abort();
    };
  }, [currentPage, isRefresh]);

  const onHandleSelectRow = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
    setCheckedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
  };

  const onHandleSelectAll = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const allCoursesIds = coursesData?.result?.map((item) => item.course_id);
      setSelectedRows(allCoursesIds);
      setCheckedRows(allCoursesIds);
    } else {
      setSelectedRows([]);
      setCheckedRows([]);
    }
  };
  const isAllSelected = selectedRows?.length === coursesData?.result?.length;

  const onCoursesReleasing = async (is_action) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "released" ? "Release" : "UnRelease"
      } the selected ${selectedRows?.length > 1 ? "courses" : "course"} ?`,
      text: "This action impact the whole course(s), and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "released" ? "Release" : "UnRelease"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetCoursesReleased(axiosPrivate, is_action, selectedRows, updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prev) => !prev);
              //
              Swal.fire(
                `${is_action === "released" ? "Release" : "UnRelease"}!`,
                `The selected course(s) has been ${
                  is_action === "released" ? "Released" : "UnReleased"
                }!`,
                "success",
              );
              setSelectedRows([]);
              setCheckedRows([]);
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  const onCoursesArchiving = async (is_action) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "archived" ? "Archive" : "UnArchive"
      } the selected ${selectedRows?.length > 1 ? "courses" : "course"} ?`,
      text: "This action impact the whole course(s), and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "archived" ? "Archive" : "UnArchive"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: `${is_action === "archived" ? "Archiving" : "UnArchiving"}...`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onGetCoursesArchived(axiosPrivate, is_action, selectedRows, updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prev) => !prev);
              //
              Swal.fire(
                `${is_action === "archived" ? "Archived" : "UnArchived"}!`,
                `The selected course(s) has been ${
                  is_action === "archived" ? "Archived" : "UnArchived"
                }!`,
                "success",
              );
              setSelectedRows([]);
              setCheckedRows([]);
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  return (
    <div className="courses">
      <div className="courses-head">
        <h1>
          Courses Management for stream of : {capitalizeAll(_stream_title)}
        </h1>
        <div className="w-full d-flex justify-space-between align-center">
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"⬅️ Back to Stream"}
            action={() => navigate(-1)}
          />
          <div className="w-fit-content d-flex gap-xs">
            <Button
              type={"button"}
              preferedStyle={"normal"}
              title={"🎓 Go to Admission Test"}
              action={() =>
                window.open(
                  `/${connectedUser?.userInfo?.sys_role}/${slugify(
                    _stream_title,
                  )}/admission/test/questions`,
                  "_blank",
                  "noreferrer",
                )
              }
            />
            {_school_id === id &&
              slugify(_school_title) === title &&
              _stream_id === st_id &&
              slugify(_stream_title) === st_title && (
                <Button
                  type={"button"}
                  preferedStyle={"default"}
                  title={"➕ Create new course"}
                  action={() =>
                    navigate(
                      `/admin/schools/${slugify(
                        _school_title,
                      )}/${_school_id}/streams/${slugify(
                        _stream_title,
                      )}/${_stream_id}/courses/create`,
                    )
                  }
                />
              )}
          </div>
          {selectedRows.length > 0 && (
            <div className="courses-actions fade-in">
              {selectedRows.length === 1 && (
                <React.Fragment>
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"👁️ Voir details"}
                    action={() => {
                      // dispatch(
                      //   configurationSlice.actions.onCurrentTeamMember(
                      //     checkedTeamMember[0]
                      //   )
                      // );
                      navigate(
                        `/admin/users/details?userId=${selectedRows[0]}`,
                      );
                    }}
                  />
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"🔄 Update infos"}
                    action={() => navigate("/admin/users/update")}
                  />
                </React.Fragment>
              )}
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"🔐 Release"}
                action={() => onCoursesReleasing("released")}
              />
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"🔐 UnRelease"}
                action={() => onCoursesReleasing("unreleased")}
              />
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"📌 Archive"}
                action={() => onCoursesArchiving("archived")}
              />
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"📌 UnArchive"}
                action={() => onCoursesArchiving("unarchived")}
              />
            </div>
          )}
        </div>
      </div>
      <div className="courses-body">
        {isEmpty(coursesData?.result) ? (
          <NotFound
            type={"data"}
            title={"No Courses found!"}
            text={"No Courses available yet."}
            isBoutton={false}
          />
        ) : (
          <React.Fragment>
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th className="col-05 text-align-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={onHandleSelectAll}
                      />
                    </th>
                    <th className="col-20 text-align-left">Date of creation</th>
                    <th className="col-25 text-align-left">Name</th>
                    <th className="col-05 text-align-center">Total Chapters</th>
                    <th className="col-10 text-align-center">Total Lessons</th>
                    <th className="col-10 text-align-center">Timing</th>
                    <th className="col-05 text-align-center">Language</th>
                    <th className="col-05 text-align-center">
                      Status(Visibility)
                    </th>
                    <th className="col-05 text-align-center">
                      Status(Deletion)
                    </th>
                    <th className="col-05 text-align-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {coursesData?.result?.map((course, idx) => {
                    return (
                      <tr
                        key={idx}
                        className={
                          checkedRows.includes(course.course_id)
                            ? "row-checked"
                            : ""
                        }
                      >
                        <td className="col-05 text-align-center">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(course?.course_id)}
                            onChange={() =>
                              onHandleSelectRow(course?.course_id)
                            }
                          />
                        </td>
                        <td className="col-20 text-align-left">
                          {moment(course?.course_created_at).format("LLLL")}
                        </td>
                        <td className="col-25 text-align-left">
                          <h3>{capitalizeAll(course?.course_title)}</h3>
                        </td>
                        <td className="col-05 text-align-center">
                          {course?.course_chapters}
                        </td>
                        <td className="col-10 text-align-center">
                          {course?.course_chapters_lessons}
                        </td>
                        <td className="col-10 text-align-center">
                          {convertDuration(course?.course_timing)}
                        </td>
                        <td className="col-05 text-align-center">
                          <StatusComponent
                            type={
                              course?.course_language === "en"
                                ? "success"
                                : "default"
                            }
                            title={
                              course?.course_language === "en"
                                ? "English"
                                : "French"
                            }
                          />
                        </td>
                        <td className="col-05 text-align-center">
                          <StatusComponent
                            type={
                              course?.course_is_activated ? "success" : "error"
                            }
                            title={
                              course?.course_is_activated
                                ? "Published"
                                : "UnPublished"
                            }
                          />
                        </td>
                        <td className="col-05 text-align-center">
                          <StatusComponent
                            type={
                              course?.course_is_deleted ? "error" : "success"
                            }
                            title={
                              course?.course_is_deleted
                                ? "Archived"
                                : "UnArchived"
                            }
                          />
                        </td>
                        <td className="col-05 text-align-center">
                          <Button
                            type={"button"}
                            preferedStyle={"default"}
                            title={`open ${truncateValue(
                              capitalizeAll(course?.course_title),
                              7,
                            )}`}
                            action={() => {
                              dispatch(
                                schoolSlice.actions.onCurrentCourse(course),
                              );
                              navigate(
                                `/admin/schools/streams/courses/${slugify(
                                  course?.course_id,
                                )}/reading?view=${slugify(
                                  course?.course_title,
                                )}`,
                              );
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <div className="p-wrapper">
                <button
                  className={
                    coursesData?.result_complements?.current_page === 1
                      ? "button is-inactive"
                      : "button is-active"
                  }
                  onClick={() => {
                    setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1));
                    setSelectedRows([]);
                  }}
                  disabled={coursesData?.result_complements?.current_page === 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of{" "}
                  {coursesData?.result_complements?.total_pages} pages.
                </span>
                <button
                  className={
                    coursesData?.result_complements?.current_page ===
                    coursesData?.result_complements?.total_pages
                      ? "button is-inactive"
                      : "button is-active"
                  }
                  onClick={() => {
                    setCurrentPage((prev) =>
                      prev <= coursesData?.result_complements?.total_pages
                        ? prev + 1
                        : prev,
                    );
                    setSelectedRows([]);
                  }}
                  disabled={
                    coursesData?.result_complements?.current_page ===
                    coursesData?.result_complements?.total_pages
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
export default AdminCourses;
